import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-level cache so multiple components don't refetch the same content.
let _cache = null;
let _inFlight = null;
const _listeners = new Set();

async function _loadAll() {
  if (_cache) return _cache;
  if (_inFlight) return _inFlight;
  _inFlight = axios
    .get(`${API}/page-content`)
    .then((r) => {
      _cache = {};
      (r.data || []).forEach((item) => { _cache[item.key] = item.value; });
      _listeners.forEach((fn) => fn(_cache));
      return _cache;
    })
    .catch(() => {
      _cache = {};
      return _cache;
    })
    .finally(() => { _inFlight = null; });
  return _inFlight;
}

export function invalidatePageContent(updates) {
  // Called after admin saves — merge into cache and notify all subscribers
  if (!_cache) _cache = {};
  Object.assign(_cache, updates || {});
  _listeners.forEach((fn) => fn(_cache));
}

/**
 * Returns the override value for `key` if any, else the default.
 * Re-renders subscribers whenever cache is updated.
 */
export default function useEditableContent(key, defaultValue = '') {
  const [value, setValue] = useState(() => (_cache?.[key] ?? defaultValue));

  useEffect(() => {
    let alive = true;
    _loadAll().then(() => {
      if (alive) setValue(_cache?.[key] ?? defaultValue);
    });
    const listener = (cache) => {
      if (alive) setValue(cache?.[key] ?? defaultValue);
    };
    _listeners.add(listener);
    return () => { alive = false; _listeners.delete(listener); };
  }, [key, defaultValue]);

  return value;
}

/**
 * Imperative save helper (used by admin manager).
 */
export const saveContent = async (key, value) => {
  const res = await axios.put(`${API}/admin/page-content/${key}`, { value }, { withCredentials: true });
  invalidatePageContent({ [key]: value });
  return res.data;
};

export const deleteContent = async (key) => {
  await axios.delete(`${API}/admin/page-content/${key}`, { withCredentials: true });
  if (_cache) delete _cache[key];
  _listeners.forEach((fn) => fn(_cache));
};
